import {
  disciplineMetadata,
  ageCategoryMetadata,
  weightCategoryMetadata,
} from "../custom-disciplines/category-metadata.js";
/**
 * Adapters that convert app-specific domain objects into the `InjectedData`
 * shape consumed by the layout renderer.
 *
 * Two contexts are supported:
 * - **Electron**: objects come from the local SQLite Prisma schema where field
 *   names are in French (`nom`, `prenom`, `dateNaissance`, `ville`, ...).
 *   They must be re-shaped into the English remote-style schema used by the
 *   layout DataSource paths (`lastname`, `firstname`, `birthDate`, `city`, ...).
 * - **NestJS (websocket-server / web-client)**: objects already come from the
 *   remote MySQL Prisma schema, so adapters are mostly thin type-guards that
 *   strip unrelated relations (e.g. nested `groups.athletes`) and fill the
 *   handful of fields that are not present on the raw Prisma payload
 *   (`selected`, `competitionFormat`, optional `powerscoutBests`).
 *
 * Every helper is pure (no I/O, no `Date.now()`, no random) so it can safely be
 * memoised by the caller.
 *
 * @see InjectedData
 * @see ./types.ts
 */

import type {
  athletePowerscoutBests,
  groups as ElectronGroup,
  ligues as ElectronLigue,
} from "@prisma-local/client";
import {
  type Ligue,
  Prisma,
  type Prisma as PrismaRemote,
} from "@prisma-remote/client";

import type {
  CompatibleLocalAgeCategory as ElectronCatAge,
  CompatibleLocalWeightCategory as ElectronCatPoid,
  CompatibleLocalDiscipline as ElectronDiscipline,
  AthletesModel,
  AthleteWithAll,
  CompatibleRemoteAgeCategory,
  CompatibleRemoteDiscipline,
  CompatibleRemoteWeightCategory,
  CurrentRefereeInfo,
  GroupModel,
  LevelRemoteWithAll,
  MeetModel,
  MeetWithAll,
  NiveauxModel,
  RecordModel,
  RecordRemoteWithAll,
  RefereeInfo,
  ThemeColors,
} from "../types.js";
import {
  athleteModelToAthleteWithAll,
  athleteWithAllToAthleteModel,
  getTotalAnnonced,
  levelLocalToLevelRemote,
  recordLocalToRecordRemote,
} from "../methods.js";

import type {
  AthleteData,
  GroupData,
  InjectedData,
  LeagueData,
  LevelData,
  MeetData,
  NextLiftData,
  OrganisationData,
  RecordData,
  RefereeLights,
} from "./types.js";
import { stabilizeInjectedData } from "./injected-data-stabilizer.js";

/**
 * Normalizes a partial organisation identity into {@link OrganisationData}.
 *
 * @param org - Raw organisation-like object (Prisma, federation snapshot, …).
 * @returns Fully shaped OrganisationData, or null if `id` is missing.
 */
export function toOrganisationData(
  org:
    | {
        id?: string | null;
        name?: string | null;
        code?: string | null;
        countryCode?: string | null;
        recordLabel?: string | null;
        recordColor?: string | null;
        recordBadgeUrl?: string | null;
        parentId?: string | null;
      }
    | null
    | undefined,
): OrganisationData | null {
  if (!org?.id) return null;
  return {
    id: org.id,
    name: org.name ?? "",
    code: org.code ?? null,
    countryCode: org.countryCode ?? null,
    recordLabel: org.recordLabel ?? null,
    recordColor: org.recordColor ?? null,
    recordBadgeUrl: org.recordBadgeUrl ?? null,
    parentId: org.parentId ?? null,
  };
}

/** Flat local-record fields used as a fallback identity source. */
type RecordOrganisationHint = {
  organisationId?: string | null;
  organisationName?: string | null;
  recordLabel?: string | null;
  recordColor?: string | null;
  recordBadgeUrl?: string | null;
  organisation?: {
    id?: string | null;
    name?: string | null;
    code?: string | null;
    countryCode?: string | null;
    recordLabel?: string | null;
    recordColor?: string | null;
    recordBadgeUrl?: string | null;
    parentId?: string | null;
  } | null;
};

/**
 * Resolves the competition organisation for layout InjectedData.
 *
 * Prefers the federation snapshot identity when it matches the meet, then a
 * nested/flat organisation carried by records, then a bare id fallback.
 *
 * @param options - Meet id, optional federation identity, optional records.
 * @returns OrganisationData or null when nothing resolvable is available.
 */
export function resolveCompetitionOrganisation(options: {
  meetOrganisationId?: string | null;
  federationOrganisation?: {
    id: string;
    name: string;
    code: string | null;
    countryCode: string | null;
    recordLabel: string | null;
    recordColor: string | null;
    recordBadgeUrl: string | null;
    parentId?: string | null;
  } | null;
  records?: RecordOrganisationHint[] | null;
}): OrganisationData | null {
  const meetId = options.meetOrganisationId ?? null;
  const fed = options.federationOrganisation
    ? toOrganisationData(options.federationOrganisation)
    : null;
  // Snapshot matches competition organiser → use full identity
  if (fed && (!meetId || fed.id === meetId)) {
    return fed;
  }
  const records = options.records ?? [];
  const matching =
    (meetId
      ? records.find(
          (r) => r.organisationId === meetId || r.organisation?.id === meetId,
        )
      : undefined) ??
    records.find((r) => r.organisationId || r.organisation?.id);
  if (matching?.organisation) {
    const fromNested = toOrganisationData(matching.organisation);
    if (fromNested && (!meetId || fromNested.id === meetId)) return fromNested;
  }
  if (
    matching?.organisationId &&
    (!meetId || matching.organisationId === meetId)
  ) {
    return toOrganisationData({
      id: matching.organisationId,
      name: matching.organisationName ?? matching.organisation?.name ?? "",
      code: matching.organisation?.code ?? null,
      countryCode: matching.organisation?.countryCode ?? null,
      recordLabel:
        matching.recordLabel ?? matching.organisation?.recordLabel ?? null,
      recordColor:
        matching.recordColor ?? matching.organisation?.recordColor ?? null,
      recordBadgeUrl:
        matching.recordBadgeUrl ??
        matching.organisation?.recordBadgeUrl ??
        null,
      parentId: matching.organisation?.parentId ?? null,
    });
  }
  if (meetId) {
    return toOrganisationData({ id: meetId, name: "" });
  }
  return fed;
}

// ---------------------------------------------------------------------------
// Common helpers
// ---------------------------------------------------------------------------

/**
 * Returns the value unchanged if it is defined, otherwise the fallback.
 *
 * Used to normalise nullable Prisma fields that the layout schema expects to
 * be present (e.g. `competitionFormat` defaults to `"FULL_POWER"`).
 *
 * @param value - Value coming from Prisma (may be `null` or `undefined`).
 * @param fallback - Value to use when `value` is nullish.
 * @returns Either `value` or `fallback`.
 */
function withFallback<T>(value: T | null | undefined, fallback: T): T {
  return value === null || value === undefined ? fallback : value;
}

function toAnnoncedTotal(athlete: AthletesModel): PrismaRemote.Decimal {
  return new Prisma.Decimal(getTotalAnnonced(athlete));
}

// ---------------------------------------------------------------------------
// Electron → InjectedData (local SQLite schema, French field names)
// ---------------------------------------------------------------------------

/**
 * Minimal shape of an Electron meet record passed to the adapters.
 *
 * This is intentionally structural (not extending the raw Prisma `meets` type)
 * so callers can pass either the Zustand store payload, a `MeetModel`, or any
 * other object that exposes those fields.
 */
export interface ElectronMeetLike {
  id: number;
  guid: string;
  name: string;
  ville: string;
  startdate: Date | string;
  enddate: Date | string;
  created_at: Date | string | null;
  ligue_id: number | null;
  /** Competition organiser id (multi-federation). */
  organisationId?: string | null;
  ligue: Pick<ElectronLigue, "id" | "nom"> | null | undefined;
  streamUrl?: string | null;
  isNational?: boolean;
  plateauStreams?: Array<{
    plateauNumber: number;
    url: string;
    id?: string;
  }> | null;
  licenseKey?: string | null;
  machineId?: string | null;
  isPublic?: boolean;
  isOver?: boolean;
}

/**
 * Optional extra fields used when converting an Electron meet into a
 * `MeetData`. They default to safe values.
 */
export interface ElectronMeetExtras {
  /** Forces the `isPublic` flag (defaults to `meet.isPublic ?? false`). */
  isPublic?: boolean;
  /** Forces the `isOver` flag (defaults to `meet.isOver ?? false`). */
  isOver?: boolean;
  /** License key used when not stored on the meet record. */
  licenseKey?: string;
}

/**
 * Converts an Electron `catAge` record into a remote-style `AgeCategory`.
 *
 * @param catAge - Local SQLite category record.
 * @returns Remote-shaped `AgeCategory`.
 */
export function electronAgeCategoryToInjected(
  catAge: ElectronCatAge,
): CompatibleRemoteAgeCategory {
  return {
    ...ageCategoryMetadata(catAge),
    id: catAge.id,
    name: catAge.nom,
    recordName: catAge.nomRecord ?? null,
    code: catAge.code ?? null,
    ageMin: catAge.ageMin,
    ageMax: catAge.ageMax,
    disciplineId: catAge.disciplineId,
    organisationId: catAge.organisationId,
    isOpen: catAge.isOpen,
    displayOrder: catAge.displayOrder,
  };
}

/**
 * Converts a list of Electron `catAge` records into remote-style age
 * categories.
 *
 * @param catAges - Local records.
 * @returns Remote-shaped age categories.
 */
export function electronAgeCategoriesToInjected(
  catAges: ElectronCatAge[],
): CompatibleRemoteAgeCategory[] {
  return catAges.map(electronAgeCategoryToInjected);
}

/**
 * Converts an Electron `catPoid` record into a remote-style `WeightCategory`.
 *
 * @param catPoid - Local SQLite category record.
 * @returns Remote-shaped `WeightCategory`.
 */
export function electronWeightCategoryToInjected(
  catPoid: ElectronCatPoid,
): CompatibleRemoteWeightCategory {
  return {
    ...weightCategoryMetadata(catPoid),
    id: catPoid.id,
    name: catPoid.nom,
    minWeight: catPoid.poidMin,
    maxWeight: catPoid.poidMax,
    isJunior: catPoid.isJunior,
    sex: catPoid.sex,
    organisationId: catPoid.organisationId,
    displayOrder: catPoid.displayOrder,
  };
}

/**
 * Converts a list of Electron `catPoid` records into remote-style weight
 * categories.
 *
 * @param catPoids - Local records.
 * @returns Remote-shaped weight categories.
 */
export function electronWeightCategoriesToInjected(
  catPoids: ElectronCatPoid[],
): CompatibleRemoteWeightCategory[] {
  return catPoids.map(electronWeightCategoryToInjected);
}

/**
 * Converts an Electron `disciplines` record into a remote-style `Discipline`.
 *
 * @param discipline - Local SQLite discipline.
 * @returns Remote-shaped `Discipline`.
 */
export function electronDisciplineToInjected(
  discipline: ElectronDiscipline,
): CompatibleRemoteDiscipline {
  return {
    ...disciplineMetadata(discipline),
    id: discipline.id,
    name: discipline.nom,
    code: discipline.code,
    competitionFormat: withFallback(discipline.competitionFormat, "FULL_POWER"),
    organisationId: discipline.organisationId,
    displayOrder: discipline.displayOrder,
  };
}

/**
 * Converts a list of Electron `disciplines` records into remote-style
 * disciplines.
 *
 * @param disciplines - Local records.
 * @returns Remote-shaped disciplines.
 */
export function electronDisciplinesToInjected(
  disciplines: ElectronDiscipline[],
): CompatibleRemoteDiscipline[] {
  return disciplines.map(electronDisciplineToInjected);
}

/**
 * Converts an Electron `ligues` record into a remote-style `LeagueData`.
 *
 * @param ligue - Local SQLite league.
 * @returns Remote-shaped `LeagueData`.
 */
export function electronLeagueToInjected(ligue: ElectronLigue): LeagueData {
  return {
    id: ligue.id,
    name: ligue.nom,
  };
}

/**
 * Converts a list of Electron `ligues` records into remote-style leagues.
 *
 * @param ligues - Local records.
 * @returns Remote-shaped leagues.
 */
export function electronLeaguesToInjected(
  ligues: ElectronLigue[],
): LeagueData[] {
  return ligues.map(electronLeagueToInjected);
}

/**
 * Converts an Electron athlete (with its category relations) into an
 * `AthleteData` ready to be consumed by the layout renderer.
 *
 * Delegates to {@link athleteModelToAthleteWithAll} which already performs all
 * French → English field renames. The result is structurally equivalent to
 * `AthleteData` since `AthleteData extends AthleteWithAll` without adding
 * extra fields.
 *
 * @param athlete - Local athlete with optional `catAge`, `catPoid`,
 *   `discipline` and `powerscoutBests` relations.
 * @returns Athlete in the remote-style shape expected by `InjectedData`.
 */
export function electronAthleteToInjected(athlete: AthletesModel): AthleteData {
  return {
    ...(athleteModelToAthleteWithAll(athlete) as AthleteData),
    totalAnnonced: toAnnoncedTotal(athlete),
  };
}

/**
 * Converts a list of Electron athletes into `AthleteData[]`.
 *
 * @param athletes - Local athletes.
 * @returns Remote-shaped athletes.
 */
export function electronAthletesToInjected(
  athletes: AthletesModel[],
): AthleteData[] {
  return athletes.map(electronAthleteToInjected);
}

/**
 * Optional extras used when converting an Electron group into `GroupData`.
 */
export interface ElectronGroupExtras {
  /** Marks the group as currently selected. Defaults to `group.selected ?? false`. */
  selected?: boolean;
  /** Parent meet id (remote uuid). Required when not derivable from `meet_id`. */
  meetId?: string;
}

/**
 * Converts an Electron group (with French field names) into a `GroupData`.
 *
 * `GroupData` is the remote `Group` minus its `athletes` relation: athletes
 * are surfaced as separate fields on `InjectedData` (`athletes`,
 * `currentGroup`), so we deliberately skip them here.
 *
 * @param group - Local group payload (Prisma local `groups` record or
 *   `GroupModel` shape).
 * @param extras - Optional overrides for fields not stored on the group row.
 * @returns Remote-shaped group without athletes.
 */
export function electronGroupToInjected(
  group: ElectronGroup | GroupModel,
  extras?: ElectronGroupExtras,
): GroupData {
  const rawMeetId = (group as { meet_id?: unknown }).meet_id;
  const meetIdFallback =
    extras?.meetId ??
    (typeof rawMeetId === "string" ? rawMeetId : String(rawMeetId ?? ""));

  const groupNumber =
    (group as { groupNumber?: number | null }).groupNumber ?? null;
  const plateauNumber =
    (group as { plateauNumber?: number | null }).plateauNumber ?? null;
  const jury = (group as { jury?: string | null }).jury ?? null;

  return {
    id: group.id,
    name: group.nom,
    meet_id: meetIdFallback,
    plateauNumber,
    groupNumber,
    observations: group.observations ?? null,
    secretary: group.secretaire ?? null,
    referee1: group.assesseur1 ?? null,
    referee2: group.assesseur2 ?? null,
    centralReferee: group.chefPlateau ?? null,
    jury,
    share: group.share ?? null,
    competitionFormat: withFallback(
      (group as { competitionFormat?: string }).competitionFormat,
      "FULL_POWER",
    ),
    customDisciplineGroupId:
      (group as { customDisciplineGroupId?: string | null })
        .customDisciplineGroupId ?? null,
    selected: extras?.selected ?? group.selected ?? false,
  };
}

/**
 * Converts a list of Electron groups into `GroupData[]`.
 *
 * @param groups - Local groups.
 * @param extras - Optional overrides applied to every group.
 * @returns Remote-shaped groups.
 */
export function electronGroupsToInjected(
  groups: (ElectronGroup | GroupModel)[],
  extras?: ElectronGroupExtras,
): GroupData[] {
  return groups.map((g) => electronGroupToInjected(g, extras));
}

/**
 * Converts an Electron meet into a `MeetData`.
 *
 * Field mapping (local → remote):
 * - `guid` → `id`
 * - `ville` → `city`
 * - `ligue.nom` → `ligue.name`
 *
 * Groups / records / levels are intentionally **not** copied: the layout
 * renderer reads them from the dedicated top-level `InjectedData` slots.
 *
 * @param meet - Local meet payload.
 * @param extras - Optional overrides for `isPublic`, `isOver`, `licenseKey`.
 * @returns Remote-shaped meet without nested relations.
 */
export function electronMeetToInjected(
  meet: ElectronMeetLike | MeetModel,
  extras?: ElectronMeetExtras,
): MeetData {
  const loose = meet as Partial<ElectronMeetLike>;
  const ligueId = meet.ligue?.id ?? meet.ligue_id ?? 0;
  const ligueName = meet.ligue?.nom ?? "";

  return {
    id: meet.guid,
    name: meet.name,
    city: meet.ville,
    startdate: toDate(meet.startdate),
    enddate: toDate(meet.enddate),
    created_at: meet.created_at ? toDate(meet.created_at) : new Date(0),
    ligue_id: ligueId,
    organisationId: meet.organisationId ?? undefined,
    licenseKey: extras?.licenseKey ?? meet.licenseKey ?? "LEGACY",
    streamUrl: meet.streamUrl ?? null,
    isNational: meet.isNational ?? false,
    themeName: null,
    plateauStreams: meet.plateauStreams ?? [],
    isPublic: extras?.isPublic ?? loose.isPublic ?? false,
    isOver: extras?.isOver ?? loose.isOver ?? false,
    ligue: { id: ligueId, name: ligueName },
  };
}

/**
 * Converts an Electron record (with its category relations) into a
 * `RecordData`. Delegates to {@link recordLocalToRecordRemote}.
 *
 * @param record - Local record with `catAge`, `catPoid`, `discipline`.
 * @returns Remote-shaped record.
 */
export function electronRecordToInjected(record: RecordModel): RecordData {
  const [remote] = recordLocalToRecordRemote([record]);
  return remote as RecordData;
}

/**
 * Converts a list of Electron records into `RecordData[]`.
 *
 * @param records - Local records.
 * @returns Remote-shaped records.
 */
export function electronRecordsToInjected(
  records: RecordModel[],
): RecordData[] {
  return recordLocalToRecordRemote(records) as RecordData[];
}

/**
 * Converts an Electron level (`niveaux`) into a `LevelData`. Delegates to
 * {@link levelLocalToLevelRemote}.
 *
 * @param level - Local level with `catAge`, `catPoid`, `discipline`.
 * @returns Remote-shaped level.
 */
export function electronLevelToInjected(level: NiveauxModel): LevelData {
  const [remote] = levelLocalToLevelRemote([level]);
  return remote as LevelData;
}

/**
 * Converts a list of Electron levels (`niveaux`) into `LevelData[]`.
 *
 * @param levels - Local levels.
 * @returns Remote-shaped levels.
 */
export function electronLevelsToInjected(levels: NiveauxModel[]): LevelData[] {
  return levelLocalToLevelRemote(levels) as LevelData[];
}

/**
 * Converts a referee info payload (the shape carried by the `currentRefereeInfo`
 * socket event - same wire format on both the Electron and NestJS sides) into
 * the `RefereeLights[]` shape exposed by `InjectedData`.
 *
 * Boolean coercion is applied so the renderer always receives `true`/`false`
 * rather than the legacy `0`/`1` values.
 *
 * @param info - Source referee info, either a `RefereeInfo[]` or a full
 *   `CurrentRefereeInfo` envelope (`{ result: RefereeInfo[] }`).
 * @returns Array of `RefereeLights` ready for the layout renderer, or `null`
 *   when the input is empty / malformed.
 */
export function refereeInfoToLights(
  info: CurrentRefereeInfo | RefereeInfo[] | null | undefined,
): RefereeLights[] | null {
  if (!info) return null;
  const items = Array.isArray(info) ? info : info.result;
  if (!Array.isArray(items)) return null;
  return items.map((entry) => ({
    type: entry.referee_type,
    passed: Boolean(entry.passed),
    color: entry.color ?? "",
    juryEnabled: Boolean(entry.juryEnabled),
  }));
}

/**
 * Input bag accepted by {@link buildInjectedDataFromElectron}.
 *
 * Every field is optional so callers can pass only what they have available
 * (e.g. an athlete display can omit `records`, `levels`, ...).
 */
export interface ElectronInjectedDataInput {
  currentMeet?: ElectronMeetLike | MeetModel | null;
  currentMeetExtras?: ElectronMeetExtras;

  currentAthlete?: AthletesModel | null;
  athletes?: AthletesModel[];

  currentGroup?: ElectronGroup | GroupModel | null;
  groups?: (ElectronGroup | GroupModel)[];
  groupExtras?: ElectronGroupExtras;

  records?: RecordModel[];
  levels?: NiveauxModel[];

  ageCategories?: ElectronCatAge[];
  weightCategories?: ElectronCatPoid[];
  disciplines?: ElectronDiscipline[];
  leagues?: ElectronLigue[];
  /** Competition organisation (meet organiser / federation snapshot identity). */
  organisation?: OrganisationData | null;

  timer?: string;
  timerSeconds?: number;
  attemptTimers?: string[];
  attemptTimerSeconds?: number[];

  currentMovement?: "squat" | "bench" | "deadlift" | null;
  attemptNumber?: number;
  placementIfSuccessful?: number | null;

  refereeLights?: CurrentRefereeInfo | RefereeInfo[] | null;
  nextLiftData?: {
    athlete: AthletesModel | null | undefined;
    movement: "squat" | "bench" | "deadlift";
    attemptNumber: number;
  } | null;

  themeColors?: ThemeColors | null;
  displaySettings?: InjectedData["displaySettings"];
}

/**
 * Builds a complete `InjectedData` object from Electron-side sources.
 *
 * Every input is optional; unspecified slots are simply omitted from the
 * returned object so it remains compatible with the renderer's optional
 * field semantics.
 *
 * @param input - Bag of Electron data points to expose to the layout.
 * @returns Layout-ready `InjectedData`.
 */
export function buildInjectedDataFromElectron(
  input: ElectronInjectedDataInput,
  previous?: InjectedData,
): InjectedData {
  const data: InjectedData = {};

  if (input.currentMeet) {
    data.currentMeet = electronMeetToInjected(
      input.currentMeet,
      input.currentMeetExtras,
    );
  } else if (input.currentMeet === null) {
    data.currentMeet = null;
  }

  if (input.currentAthlete) {
    data.currentAthlete = electronAthleteToInjected(input.currentAthlete);
  } else if (input.currentAthlete === null) {
    data.currentAthlete = null;
  }

  if (input.athletes) {
    data.athletes = electronAthletesToInjected(input.athletes);
  }

  if (input.currentGroup) {
    data.currentGroup = electronGroupToInjected(
      input.currentGroup,
      input.groupExtras,
    );
  } else if (input.currentGroup === null) {
    data.currentGroup = null;
  }

  if (input.groups) {
    data.groups = electronGroupsToInjected(input.groups, input.groupExtras);
  }

  if (input.records) data.records = electronRecordsToInjected(input.records);
  if (input.levels) data.levels = electronLevelsToInjected(input.levels);

  if (input.ageCategories) {
    data.ageCategories = electronAgeCategoriesToInjected(input.ageCategories);
  }
  if (input.weightCategories) {
    data.weightCategories = electronWeightCategoriesToInjected(
      input.weightCategories,
    );
  }
  if (input.disciplines) {
    data.disciplines = electronDisciplinesToInjected(input.disciplines);
  }
  if (input.leagues) {
    data.leagues = electronLeaguesToInjected(input.leagues);
  }
  if (input.organisation !== undefined) {
    data.organisation = input.organisation;
  }

  if (input.timer !== undefined) data.timer = input.timer;
  if (input.timerSeconds !== undefined) data.timerSeconds = input.timerSeconds;
  if (input.attemptTimers !== undefined)
    data.attemptTimers = input.attemptTimers;
  if (input.attemptTimerSeconds !== undefined) {
    data.attemptTimerSeconds = input.attemptTimerSeconds;
  }
  if (input.currentMovement !== undefined) {
    data.currentMovement = input.currentMovement;
  }
  if (input.attemptNumber !== undefined) {
    data.attemptNumber = input.attemptNumber;
  }
  if (input.placementIfSuccessful !== undefined) {
    data.placementIfSuccessful = input.placementIfSuccessful;
  }

  if (input.refereeLights !== undefined) {
    data.refereeLights = refereeInfoToLights(input.refereeLights);
  }

  if (input.nextLiftData !== undefined) {
    data.nextLiftData = mapElectronNextLift(input.nextLiftData);
  }

  if (input.themeColors !== undefined) data.themeColors = input.themeColors;
  if (input.displaySettings !== undefined)
    data.displaySettings = input.displaySettings;

  return stabilizeInjectedData(previous, data);
}

function mapElectronNextLift(
  value: ElectronInjectedDataInput["nextLiftData"],
): NextLiftData | null {
  if (!value || !value.athlete) return null;
  return {
    athlete: electronAthleteToInjected(value.athlete),
    movement: value.movement,
    attemptNumber: value.attemptNumber,
  };
}

// ---------------------------------------------------------------------------
// NestJS / remote → InjectedData
// ---------------------------------------------------------------------------

/** Athlete payload returned by the remote Prisma client with required relations. */
export type NestAthletePayload = PrismaRemote.AthleteGetPayload<{
  include: {
    ageCategory: true;
    discipline: true;
    weightCategory: true;
  };
}>;

/** Group payload returned by the remote Prisma client (no athletes nested). */
export type NestGroupPayload = PrismaRemote.GroupGetPayload<object>;

/** Group payload returned with its nested athletes (used when destructuring). */
export type NestGroupWithAthletesPayload = PrismaRemote.GroupGetPayload<{
  include: {
    athletes: {
      include: {
        ageCategory: true;
        discipline: true;
        weightCategory: true;
      };
    };
  };
}>;

/** Meet payload returned with its `ligue` relation. */
export type NestMeetPayload = PrismaRemote.MeetGetPayload<{
  include: { ligue: true };
}>;

/** Record payload returned with category + organisation relations. */
export type NestRecordPayload = PrismaRemote.RecordGetPayload<{
  include: {
    ageCategory: true;
    weightCategory: true;
    discipline: true;
    organisation: true;
  };
}>;

/** Level payload returned with its category relations. */
export type NestLevelPayload = PrismaRemote.LevelGetPayload<{
  include: { ageCategory: true; weightCategory: true; discipline: true };
}>;

/**
 * Converts a remote-Prisma athlete payload into an `AthleteData`.
 *
 * Since the remote schema is already English-named, this is essentially a
 * structural cast. Optional `powerscoutBests` may be attached if it has been
 * resolved from the local PowerScout database.
 *
 * @param athlete - Athlete fetched with `ageCategory`, `discipline`,
 *   `weightCategory` relations included.
 * @param powerscoutBests - Optional Powerscout bests record fetched
 *   separately from the local SQLite database.
 * @returns Layout-ready athlete.
 */
export function nestAthleteToInjected(
  athlete: NestAthletePayload | AthleteWithAll,
  powerscoutBests?: athletePowerscoutBests | null,
): AthleteData {
  const athleteWithAll = athlete as AthleteWithAll;
  const athleteModel = athleteWithAllToAthleteModel(athleteWithAll);
  return {
    ...athlete,
    totalAnnonced: toAnnoncedTotal(athleteModel),
    powerscoutBests: powerscoutBests ?? athleteWithAll.powerscoutBests ?? null,
  } as AthleteData;
}

/**
 * Converts a list of remote-Prisma athletes into `AthleteData[]`.
 *
 * @param athletes - Athletes fetched with required relations. When the payload
 *   already carries a local Powerscout overlay (live meet snapshots from
 *   Electron), `powerscoutBests` is preserved on each {@link AthleteData}.
 */
export function nestAthletesToInjected(
  athletes: (NestAthletePayload | AthleteWithAll)[],
): AthleteData[] {
  return athletes.map((a) => nestAthleteToInjected(a));
}

/** Optional fields not present on the raw Prisma `Group` row. */
export interface NestGroupExtras {
  /** Marks the group as currently selected (defaults to `false`). */
  selected?: boolean;
}

/**
 * Converts a remote-Prisma group payload into a `GroupData`.
 *
 * The remote `Group` schema already exposes English field names matching
 * `GroupData`. The adapter only injects the runtime-only `selected` flag and
 * strips any nested `athletes` relation that may have been included.
 *
 * @param group - Remote group payload (with or without nested athletes).
 * @param extras - Optional `selected` flag.
 * @returns Layout-ready group without athletes.
 */
export function nestGroupToInjected(
  group: NestGroupPayload | NestGroupWithAthletesPayload,
  extras?: NestGroupExtras,
): GroupData {
  const { ...rest } = group as NestGroupPayload & { athletes?: unknown };
  if ("athletes" in rest) {
    delete (rest as { athletes?: unknown }).athletes;
  }
  return {
    ...(rest as NestGroupPayload),
    selected: extras?.selected ?? false,
  } as GroupData;
}

/**
 * Converts a list of remote-Prisma groups into `GroupData[]`.
 *
 * @param groups - Remote groups.
 * @param extras - Optional `selected` flag applied to every group.
 * @returns Layout-ready groups.
 */
export function nestGroupsToInjected(
  groups: (NestGroupPayload | NestGroupWithAthletesPayload)[],
  extras?: NestGroupExtras,
): GroupData[] {
  return groups.map((g) => nestGroupToInjected(g, extras));
}

/**
 * Converts a remote-Prisma meet payload into a `MeetData`.
 *
 * Any nested `groups` / `records` / `niveaux` relations are stripped because
 * `InjectedData` exposes them via dedicated top-level slots.
 *
 * @param meet - Remote meet with at least its `ligue` relation included.
 * @returns Layout-ready meet.
 */
export function nestMeetToInjected(
  meet: NestMeetPayload | MeetWithAll,
): MeetData {
  const { ...rest } = meet as NestMeetPayload & {
    groups?: unknown;
    records?: unknown;
    niveaux?: unknown;
  };
  delete (rest as { groups?: unknown }).groups;
  delete (rest as { records?: unknown }).records;
  delete (rest as { niveaux?: unknown }).niveaux;
  return rest as MeetData;
}

/**
 * Converts a remote-Prisma record into a `RecordData`.
 *
 * Ensures `organisation` carries every layout path field (including `parentId`)
 * even when the Prisma include only returned a subset of identity columns.
 *
 * @param record - Remote record with category (+ optional organisation) relations.
 * @returns Layout-ready record.
 */
export function nestRecordToInjected(
  record: NestRecordPayload | RecordRemoteWithAll,
): RecordData {
  const base = record as RecordRemoteWithAll;
  const organisation =
    toOrganisationData(base.organisation) ??
    (base.organisationId
      ? toOrganisationData({ id: base.organisationId, name: "" })
      : null);
  return {
    ...base,
    organisationId: base.organisationId,
    organisation,
  } as RecordData;
}

/**
 * Converts a list of remote-Prisma records into `RecordData[]`.
 *
 * @param records - Remote records.
 * @returns Layout-ready records.
 */
export function nestRecordsToInjected(
  records: (NestRecordPayload | RecordRemoteWithAll)[],
): RecordData[] {
  return records.map(nestRecordToInjected);
}

/**
 * Converts a remote-Prisma level into a `LevelData`. Identity cast since the
 * schemas already match.
 *
 * @param level - Remote level with category relations included.
 * @returns Layout-ready level.
 */
export function nestLevelToInjected(
  level: NestLevelPayload | LevelRemoteWithAll,
): LevelData {
  return level as LevelRemoteWithAll as LevelData;
}

/**
 * Converts a list of remote-Prisma levels into `LevelData[]`.
 *
 * @param levels - Remote levels.
 * @returns Layout-ready levels.
 */
export function nestLevelsToInjected(
  levels: (NestLevelPayload | LevelRemoteWithAll)[],
): LevelData[] {
  return levels.map(nestLevelToInjected);
}

/**
 * Converts a remote-Prisma `Ligue` row into a `LeagueData`. Identity cast.
 *
 * @param ligue - Remote `Ligue`.
 * @returns Layout-ready league.
 */
export function nestLeagueToInjected(ligue: Ligue): LeagueData {
  return ligue as LeagueData;
}

/**
 * Converts a list of remote-Prisma `Ligue` rows into `LeagueData[]`.
 *
 * @param ligues - Remote leagues.
 * @returns Layout-ready leagues.
 */
export function nestLeaguesToInjected(ligues: Ligue[]): LeagueData[] {
  return ligues.map(nestLeagueToInjected);
}

/**
 * Input bag accepted by {@link buildInjectedDataFromNest}.
 */
export interface NestInjectedDataInput {
  /** Accepts Prisma payloads or federation-aware util types (optional organisationId). */
  currentMeet?: NestMeetPayload | MeetWithAll | null;

  currentAthlete?: NestAthletePayload | AthleteWithAll | null;
  currentAthletePowerscoutBests?: athletePowerscoutBests | null;
  athletes?: (NestAthletePayload | AthleteWithAll)[];

  currentGroup?: NestGroupPayload | NestGroupWithAthletesPayload | null;
  groups?: (NestGroupPayload | NestGroupWithAthletesPayload)[];
  groupExtras?: NestGroupExtras;

  records?: (NestRecordPayload | RecordRemoteWithAll)[];
  levels?: (NestLevelPayload | LevelRemoteWithAll)[];

  ageCategories?: CompatibleRemoteAgeCategory[];
  weightCategories?: CompatibleRemoteWeightCategory[];
  disciplines?: CompatibleRemoteDiscipline[];
  leagues?: Ligue[];
  /** Competition organisation (meet.organisationId identity). */
  organisation?: OrganisationData | null;

  timer?: string;
  timerSeconds?: number;
  attemptTimers?: string[];
  attemptTimerSeconds?: number[];

  currentMovement?: "squat" | "bench" | "deadlift" | null;
  attemptNumber?: number;
  placementIfSuccessful?: number | null;

  /**
   * Raw payload from the `currentRefereeInfo` socket event. The NestJS gateway
   * only relays this event without transforming it (see
   * `app.gateaway.ts#handleCurrentRefereeInfo`), so the wire format is the
   * same as on the Electron side.
   */
  refereeLights?: CurrentRefereeInfo | RefereeInfo[] | null;
  nextLiftData?: NextLiftData | null;

  themeColors?: ThemeColors | null;
  displaySettings?: InjectedData["displaySettings"];
}

/**
 * Builds a complete `InjectedData` object from NestJS / remote-Prisma sources.
 *
 * Every input is optional; unspecified slots are simply omitted.
 *
 * @param input - Bag of remote data points to expose to the layout.
 * @returns Layout-ready `InjectedData`.
 */
export function buildInjectedDataFromNest(
  input: NestInjectedDataInput,
  previous?: InjectedData,
): InjectedData {
  const data: InjectedData = {};

  if (input.currentMeet) {
    data.currentMeet = nestMeetToInjected(input.currentMeet);
  } else if (input.currentMeet === null) {
    data.currentMeet = null;
  }

  if (input.currentAthlete) {
    data.currentAthlete = nestAthleteToInjected(
      input.currentAthlete,
      input.currentAthletePowerscoutBests,
    );
  } else if (input.currentAthlete === null) {
    data.currentAthlete = null;
  }

  if (input.athletes) data.athletes = nestAthletesToInjected(input.athletes);

  if (input.currentGroup) {
    data.currentGroup = nestGroupToInjected(
      input.currentGroup,
      input.groupExtras,
    );
  } else if (input.currentGroup === null) {
    data.currentGroup = null;
  }

  if (input.groups) {
    data.groups = nestGroupsToInjected(input.groups, input.groupExtras);
  }

  if (input.records) data.records = nestRecordsToInjected(input.records);
  if (input.levels) data.levels = nestLevelsToInjected(input.levels);

  if (input.ageCategories) data.ageCategories = input.ageCategories;
  if (input.weightCategories) data.weightCategories = input.weightCategories;
  if (input.disciplines) data.disciplines = input.disciplines;
  if (input.leagues) data.leagues = nestLeaguesToInjected(input.leagues);
  if (input.organisation !== undefined) {
    data.organisation = input.organisation;
  }

  if (input.timer !== undefined) data.timer = input.timer;
  if (input.timerSeconds !== undefined) data.timerSeconds = input.timerSeconds;
  if (input.attemptTimers !== undefined)
    data.attemptTimers = input.attemptTimers;
  if (input.attemptTimerSeconds !== undefined) {
    data.attemptTimerSeconds = input.attemptTimerSeconds;
  }
  if (input.currentMovement !== undefined) {
    data.currentMovement = input.currentMovement;
  }
  if (input.attemptNumber !== undefined) {
    data.attemptNumber = input.attemptNumber;
  }
  if (input.placementIfSuccessful !== undefined) {
    data.placementIfSuccessful = input.placementIfSuccessful;
  }

  if (input.refereeLights !== undefined) {
    data.refereeLights = refereeInfoToLights(input.refereeLights);
  }
  if (input.nextLiftData !== undefined) data.nextLiftData = input.nextLiftData;
  if (input.themeColors !== undefined) data.themeColors = input.themeColors;
  if (input.displaySettings !== undefined)
    data.displaySettings = input.displaySettings;

  return stabilizeInjectedData(previous, data);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}
